<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  import { InlineNotification, Modal } from 'carbon-components-svelte';
  import type { InlineNotificationProps } from 'carbon-components-svelte/src/Notification/InlineNotification.svelte';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import { updateSLATask } from '../../../services';
  import { getServerErrorMessage } from '../../../utils';

  export let slaTaskName = '',
    slaTaskId: string,
    active = true,
    open = false;

  let loading = false,
    resultNotification: Required<Pick<InlineNotificationProps, 'title' | 'subtitle' | 'kind'>> | null = null;

  const dispatch = createEventDispatcher<{
    close: { action: 'disable-task' | 'enable-task'; data: { id: string; friendlyName: string } } | undefined;
  }>();

  const action = active ? 'disable' : 'enable';

  async function onSubmit(): Promise<void> {
    loading = true;
    resultNotification = null;

    try {
      await updateSLATask(slaTaskId, { active: !active });
      dispatch('close', { action: `${action}-task`, data: { id: slaTaskId, friendlyName: slaTaskName } });
    } catch (error) {
      const errorMessage = getServerErrorMessage(error) ?? 'Please try again later.';
      console.error('[ToggleSlaTaskModal] Failed to toggle SLA task status!', errorMessage);

      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: `Failed to ${action} SLA task! ${errorMessage}`,
      };
    } finally {
      loading = false;
    }
  }

  function onModalClose() {
    dispatch('close');
  }
</script>

<Modal
  modalHeading="{active ? 'Disable' : 'Enable'} SLA Task"
  primaryButtonText={active ? 'Disable' : 'Enable'}
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside={true}
  bind:open
  on:click:button--secondary={onModalClose}
  on:close={onModalClose}
  on:submit={onSubmit}
>
  <LoadingSpinner {loading}>
    {#if resultNotification}
      <InlineNotification
        hideCloseButton
        kind={resultNotification.kind}
        lowContrast
        title={resultNotification.title}
        subtitle={resultNotification.subtitle}
      />
    {/if}

    {#if active}
      <p>You are about to <strong>disable</strong> the SLA task <strong>{slaTaskName}</strong>.</p>
      <p>While disabled, this task will not trigger any SLA alerts.</p>
    {:else}
      <p>You are about to <strong>enable</strong> the SLA task <strong>{slaTaskName}</strong>.</p>
      <p>Once enabled, this task will resume triggering SLA alerts as configured.</p>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  p {
    margin: 0 0 0.75rem;
  }
</style>
